import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { ExportReportType, ExportRequest } from '../../utilities/reportExport';

export type ReportExportStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';

/** An export report being built by the API. */
export interface ReportExportJob {
  id: string;
  status: ReportExportStatus;
  /** Why the export failed, when it did: one of the API's report export error codes. */
  errorCode?: string | null;
  /** How many reports the finished PDF lists. */
  reportCount?: number | null;
  /** The name to save the finished PDF as: <region>-<organization>-<date>.pdf. */
  fileName?: string | null;
}

interface ReportExportResponse {
  data: {
    export: ReportExportJob;
  };
}

/** Thrown when the API could not build the report; `errorCode` says why. */
export class ReportExportFailedError extends Error {
  constructor(public readonly errorCode: string | null) {
    super(`The report could not be built${errorCode ? ` (${errorCode})` : ''}`);
  }
}

/** Thrown when the report is still not ready after waiting as long as we will. */
export class ReportExportTimedOutError extends Error {
  readonly errorCode = 'REPORT_EXPORT_TIMED_OUT_EXCEPTION';

  constructor() {
    super('The report took too long to build');
  }
}

const POLL_INTERVAL_MS = 2000;
const GIVE_UP_AFTER_MS = 10 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class ReportExportService {
  constructor(private apiService: ApiService) {}

  /**
   * Asks the API to build an export report. It is built in the background: ask about the returned job until it is
   * completed (see {@link waitUntilDone}), then {@link fetchPdf}.
   */
  async start(
    organizationId: string,
    reportType: ExportReportType,
    request: ExportRequest,
  ): Promise<ReportExportJob> {
    const response = await this.apiService.post<ReportExportResponse>(
      `/organization-report/${organizationId}/exports?reportType=${reportType}`,
      request,
    );
    return response.data.export;
  }

  async get(jobId: string): Promise<ReportExportJob> {
    const response = await this.apiService.get<ReportExportResponse>(`/organization-report/exports/${jobId}`);
    return response.data.export;
  }

  fetchPdf(jobId: string): Promise<Blob> {
    return this.apiService.getBlob(`/organization-report/exports/${jobId}/pdf`);
  }

  /**
   * Asks about the export until it is done.
   *
   * @throws ReportExportFailedError if the API could not build it
   * @throws ReportExportTimedOutError if it is still not done after `giveUpAfterMs`
   */
  async waitUntilDone(
    jobId: string,
    options: { intervalMs?: number; giveUpAfterMs?: number; sleep?: (ms: number) => Promise<void> } = {},
  ): Promise<ReportExportJob> {
    const intervalMs = options.intervalMs ?? POLL_INTERVAL_MS;
    const giveUpAfterMs = options.giveUpAfterMs ?? GIVE_UP_AFTER_MS;
    const sleep = options.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));

    let waitedMs = 0;
    while (true) {
      const job = await this.get(jobId);

      if (job.status === 'COMPLETED') {
        return job;
      }
      if (job.status === 'FAILED') {
        throw new ReportExportFailedError(job.errorCode ?? null);
      }
      if (waitedMs >= giveUpAfterMs) {
        throw new ReportExportTimedOutError();
      }

      await sleep(intervalMs);
      waitedMs += intervalMs;
    }
  }
}
